/**
 * The layout shell every signal-flow panel is built on.
 *
 * A CSS grid rather than a row of matched-height flex columns: lanes have to
 * line up across every stage, and grid does that from the track definition
 * alone. Nothing here sets a row height, so a lane that grows — a stack card
 * carrying a deployment failure, say — pushes its whole row down and stays
 * aligned with its connectors. Columns are fluid for the same reason: the panel
 * renders inside a table detail row on Simulive and full width on the Test lab.
 */

import { Fragment, type ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import { flowToken } from './tokens';

/** One horizontal lane through the diagram: a feed, camera or stack. */
export interface FlowLane {
  key: string;
}

export type FlowCell<L extends FlowLane> =
  /** A cell per lane, aligned with that same lane in every other column. */
  | { span: 'lane'; render: (lane: L, index: number) => ReactNode }
  /**
   * One cell beside every lane at once — how a shared resource (the gateway,
   * the S3 recording) sits next to the per-feed rows it serves.
   */
  | { span: 'lanes'; render: () => ReactNode };

export interface FlowColumn<L extends FlowLane> {
  key: string;
  /**
   * Column heading. Connector columns leave it out and keep the empty band, so
   * their lines start level with the cards beside them.
   */
  title?: ReactNode;
  /**
   * Grid track size. `auto` hugs its content; `minmax(<min>, <n>fr)` is what
   * lets the panel breathe without letting a card collapse.
   */
  width: string;
  cell: FlowCell<L>;
}

interface SignalFlowGridProps<L extends FlowLane> {
  columns: Array<FlowColumn<L>>;
  lanes: L[];
}

export default function SignalFlowGrid<L extends FlowLane>({
  columns,
  lanes,
}: SignalFlowGridProps<L>) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: columns.map((column) => column.width).join(' '),
        rowGap: 1,
        alignItems: 'stretch',
        // Fluid tracks still bottom out. Past that point scrolling the diagram
        // beats crushing every card to unreadable width.
        overflowX: 'auto',
      }}
    >
      {columns.map((column, columnIndex) => (
        <Fragment key={column.key}>
          <Box
            sx={{
              gridColumn: columnIndex + 1,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              minHeight: 34,
              px: column.title ? 0 : 1.5,
            }}
          >
            {column.title && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: flowToken(theme, 'wash500'),
                }}
              >
                {column.title}
              </Typography>
            )}
          </Box>

          {column.cell.span === 'lanes' ? (
            <Box
              sx={{
                gridColumn: columnIndex + 1,
                // `span 1` when there are no lanes: a zero span is invalid CSS
                // and would drop the cell out of the grid entirely.
                gridRow: `2 / span ${Math.max(1, lanes.length)}`,
                display: 'flex',
              }}
            >
              {column.cell.render()}
            </Box>
          ) : (
            lanes.map((lane, laneIndex) => {
              const { render } = column.cell as Extract<
                FlowCell<L>,
                { span: 'lane' }
              >;

              return (
                <Box
                  key={lane.key}
                  sx={{
                    gridColumn: columnIndex + 1,
                    gridRow: laneIndex + 2,
                    display: 'flex',
                    alignItems: 'stretch',
                  }}
                >
                  {render(lane, laneIndex)}
                </Box>
              );
            })
          )}
        </Fragment>
      ))}
    </Box>
  );
}
